import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  Typography,
  useTheme,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import { Columns2 } from './SponsorAdminColumns';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import CreateAdmin from '../CreateSponsor/CreateAdmin';
import CustomCard from '../../components/@extended/CustomCard';
import CustomButton from '../../components/@extended/CustomButton';
import UserManagementDialog from './UserManagementDialog';

// User type display labels
const USER_TYPE_LABELS = {
  data_manager: 'Data Manager',
  site_monitor: 'Site Monitor',
};

// User type chip colors
const USER_TYPE_COLORS = {
  data_manager: 'primary',
  site_monitor: 'secondary',
};

const DataManagerTable = ({ open, data, isLoading, showUserTypeDropdown = true }) => {
  const theme = useTheme();
  const columns = Columns2();
  const { sponsorId } = useParams();

  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleUserClick = (user) => {
    // Pass the user object from row.user
    setSelectedUser(user?.user);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleUserUpdate = () => {
    // Handle any refresh logic if needed
    console.log('User updated successfully');
  };

  if (isLoading) {
    return <></>;
  }

  // Enhanced columns with User Type and Actions columns
  const enhancedColumns = [
    ...columns,
    {
      id: 'userType',
      label: 'User Type',
      style: { minWidth: 130 },
      align: 'left',
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'center',
      style: { minWidth: 100 },
    },
  ];

  // Helper function to get user type from row data
  const getUserType = (row) => {
    return row?.userType || row?.user?.userMetaInfo?.userType || 'data_manager';
  };

  return (
    <Box>
      <CustomCard
        title={
          <Typography variant="h6" fontWeight={600} color={theme.palette.grey[800]}>
            Data Managers & Site Monitors
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'center' }}>
            <CreateAdmin 
              open={open} 
              sponsorId={sponsorId} 
              type="sponsor_level_user" 
              showUserTypeDropdown={showUserTypeDropdown}
            />
          </Box>
        }
      >
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <CustomTableHead>
              <TableRow>
                {enhancedColumns.map((column) => (
                  <CustomTableHeadCell key={column.id} align={column.align} sx={{ ...column.style }}>
                    {column.label}
                  </CustomTableHeadCell>
                ))}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {data && data.length > 0 ? (
                data.map((row) => {
                  const userType = getUserType(row);
                  return (
                    <CustomTableRow key={row?.id} hover role="checkbox" tabIndex={-1}>
                      <TableCell>
                        {row?.user?.firstName} {row?.user?.lastName}
                      </TableCell>
                      <TableCell>{row?.user?.email}</TableCell>
                      <TableCell>{row?.user?.contactNumber}</TableCell>
                      <TableCell>{moment(row?.createdAt).format('DD/MM/YYYY')}</TableCell>
                      <TableCell>
                        <Chip
                          label={USER_TYPE_LABELS[userType] || userType}
                          color={USER_TYPE_COLORS[userType] || 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          aria-label="manage user"
                          onClick={() => handleUserClick(row)}
                          sx={{
                            color: theme.palette.grey[600],
                            '&:hover': {
                              backgroundColor: theme.palette.grey[100],
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </CustomTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={enhancedColumns.length}>
                    <Typography variant="subtitle1" color="initial">
                      No Data Managers or Site Monitors Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <UserManagementDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          user={selectedUser}
          sponsorId={sponsorId}
          onUserUpdate={handleUserUpdate}
        />
      </CustomCard>
    </Box>
  );
};

export default DataManagerTable;

